import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import kycService from "../../../services/api/kyc.service";
import DocumentViewer from "../Admindashboard/DocumentViewer";
import { useNavigation } from "@/contexts/notification/NavigationContext";

const ReviewSubmission = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    basicInfo: {},
    dematDetails: {},
    bankDetails: {},
    documents: {
      panCard: false,
      bankDocuments: false,
      dematDocuments: false,
      aadhaarFront: false,
      aadhaarBack: false,
    },
  });
  const [loadingStates, setLoadingStates] = useState({
    panCard: false,
    bankDocuments: false,
    dematDocuments: false,
    aadhaarFront: false,
    aadhaarBack: false,
  });

  // State for document viewer
  const [viewerState, setViewerState] = useState({
    isOpen: false,
    kycId: null,
    documentType: null,
    documentName: null,
  });

  // State to track missing fields
  const [missingFields, setMissingFields] = useState([]);
  
  // State to track KYC Status so we can lock submission
  const [kycStatus, setKycStatus] = useState(null);

  // Use the navigation context to get the last visited page
  const { getLastMeaningfulPage, clearPendingPurchase } = useNavigation();

  useEffect(() => {
    const loadSubmittedData = async () => {
      try {
        const profileData = await kycService.getProfile();
        const savedData = kycService.getSavedFormData();
        const kycDetails = profileData.kycDetails || {};

        // Store the kycId for document viewing
        if (kycDetails.kycId) {
          setViewerState((prev) => ({
            ...prev,
            kycId: kycDetails.kycId,
          }));
        }

        // Set KYC Status
        if (kycDetails.verificationStatus) {
          setKycStatus(kycDetails.verificationStatus);
        }

        // More robust document status checking
        const determineDocumentStatus = (docType) => {
          if (typeof savedData?.documents?.[docType] === "boolean") {
            return savedData.documents[docType];
          }
          if (savedData?.documents?.[docType]?.data) {
            return true;
          }
          if (typeof kycDetails.documents?.[docType] === "boolean") {
            return kycDetails.documents[docType];
          }
          if (kycDetails.documents?.[docType]?.data) {
            return true;
          }
          return false;
        };

        const documentsStatus = {
          panCard: determineDocumentStatus("panCard"),
          bankDocuments: determineDocumentStatus("bankDocuments"),
          dematDocuments: determineDocumentStatus("dematDocuments"),
          aadhaarFront: determineDocumentStatus("aadhaarFront"),
          aadhaarBack: determineDocumentStatus("aadhaarBack"),
        };

        // Enhanced form data with all missing fields
        const newFormData = {
          basicInfo: {
            email: profileData.email || "",
            mobileNo: profileData.mobileNo || "",
            fullName: profileData.fullName || "",
            panNumber: kycDetails.panNumber || profileData.panNumber || "",
            // ADD: Date of Birth
            dateOfBirth: kycDetails.dateOfBirth || "",
            line1:
              kycDetails.address?.line1 || profileData.address?.line1 || "",
            line2:
              kycDetails.address?.line2 || profileData.address?.line2 || "",
            city: kycDetails.address?.city || profileData.address?.city || "",
            state:
              kycDetails.address?.state || profileData.address?.state || "",
            pincode:
              kycDetails.address?.pincode || profileData.address?.pincode || "",
            country:
              kycDetails.address?.country || profileData.address?.country || "",
            nationality:
              kycDetails.nationality || profileData.nationality || "",
          },
          dematDetails: {
            // Existing fields
            dpId: kycDetails.dematDetails?.dpId || "",
            clientId: kycDetails.dematDetails?.clientId || "",
            // ADD: Missing demat fields
            depository: kycDetails.dematDetails?.depository || "",
            dan: kycDetails.dematDetails?.dan || "",
          },
          bankDetails: {
            // Existing fields
            bankName: kycDetails.bankDetails?.bankName || "",
            accountNumber: kycDetails.bankDetails?.accountNumber || "",
            ifscCode: kycDetails.bankDetails?.ifscCode || "",
            // ADD: Missing bank fields
            accountName: kycDetails.bankDetails?.accountName || "",
            accountType: kycDetails.bankDetails?.accountType || "",
          },
          documents: documentsStatus,
          // ADD: KYC Flow Details section
          kycFlowDetails: {
            idDetails: {
              documentType:
                kycDetails.kycFlowDetails?.idDetails?.documentType || "",
              flowType: kycDetails.kycFlowDetails?.idDetails?.flowType || "",
              metadata: kycDetails.kycFlowDetails?.idDetails?.metadata || {},
            },
            bankAccountDetails: {
              documentType:
                kycDetails.kycFlowDetails?.bankAccountDetails?.documentType ||
                "",
              flowType:
                kycDetails.kycFlowDetails?.bankAccountDetails?.flowType || "",
              metadata:
                kycDetails.kycFlowDetails?.bankAccountDetails?.metadata || {},
            },
            dematAccountDetails: {
              documentType:
                kycDetails.kycFlowDetails?.dematAccountDetails?.documentType ||
                "",
              flowType:
                kycDetails.kycFlowDetails?.dematAccountDetails?.flowType || "",
              metadata:
                kycDetails.kycFlowDetails?.dematAccountDetails?.metadata || {},
            },
          },
        };

        setFormData(newFormData);
        checkMissingFields(newFormData);
      } catch (error) {
        console.error("Failed to load data:", error);
        toast.error("Failed to load submitted data");
      }
    };

    loadSubmittedData();
  }, []);

  // Modified to open the document viewer instead of a new window
  const handleViewDocument = async (documentType, documentName) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [documentType]: true }));

      const profileData = await kycService.getProfile();
      const kycId = profileData?.kycDetails?.kycId;

      if (!kycId) {
        toast.error("KYC record not found");
        return;
      }

      setViewerState({
        isOpen: true,
        kycId,
        documentType,
        documentName: documentName || documentType,
      });
    } catch (error) {
      toast.error(error.message || "Failed to prepare document for viewing");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [documentType]: false }));
    }
  };

  const closeViewer = () => {
    setViewerState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const handleFinalSubmit = async () => {
    if (!allRequiredFieldsFilled()) {
      checkMissingFields(formData);
      toast.error("Please complete all required fields before submitting", {
        style: {
          background: "#EF4444",
          color: "#FFFFFF",
        },
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileData = await kycService.getProfile();

      if (!profileData?.kycDetails?.kycId) {
        throw new Error("KYC ID not found");
      }

      const finalFormData = {
        type: "final",
        fullName: formData.basicInfo.fullName,
        email: formData.basicInfo.email,
        mobileNo: formData.basicInfo.mobileNo,
        panNumber: formData.basicInfo.panNumber,
        dateOfBirth: formData.basicInfo.dateOfBirth, // ADD: Include DOB
        nationality: formData.basicInfo.nationality,
        address: {
          line1: formData.basicInfo.line1,
          line2: formData.basicInfo.line2,
          city: formData.basicInfo.city,
          state: formData.basicInfo.state,
          pincode: formData.basicInfo.pincode,
          country: formData.basicInfo.country,
        },
        dematDetails: formData.dematDetails,
        bankDetails: formData.bankDetails,
        // ADD: Include KYC flow details in submission
        kycFlowDetails: formData.kycFlowDetails,
      };

      const response = await kycService.processStep(5, finalFormData);

      if (response.success === true || response.success === "true") {
        clearPendingPurchase();
        toast.success("Your KYC has been submitted successfully!", {
     
        });
        kycService.clearSavedFormData();
        navigate("/dashboard");
      } else {
        throw new Error(response.message || "Submission failed");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Failed to submit KYC", {
        style: {
          background: "#EF4444",
          color: "#FFFFFF",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // Enhanced validation with all required fields
  const allRequiredFieldsFilled = () => {
    const basicInfo = formData.basicInfo;
    if (
      !basicInfo.fullName ||
      !basicInfo.email ||
      !basicInfo.mobileNo ||
      !basicInfo.panNumber ||
      !basicInfo.dateOfBirth || // ADD: DOB validation
      !basicInfo.line1 ||
      !basicInfo.city ||
      !basicInfo.state ||
      !basicInfo.pincode ||
      !basicInfo.country ||
      !basicInfo.nationality
    ) {
      return false;
    }

    // Enhanced demat validation
    if (
      !formData.dematDetails.dpId ||
      !formData.dematDetails.clientId ||
      !formData.dematDetails.depository // ADD: Depository validation
    ) {
      return false;
    }

    // Enhanced bank validation
    if (
      !formData.bankDetails.bankName ||
      !formData.bankDetails.accountNumber ||
      !formData.bankDetails.ifscCode ||
      !formData.bankDetails.accountName || // ADD: Account name validation
      !formData.bankDetails.accountType // ADD: Account type validation
    ) {
      return false;
    }

    if (
      !formData.documents.panCard ||
      !formData.documents.bankDocuments ||
      !formData.documents.dematDocuments ||
      !formData.documents.aadhaarFront ||
      !formData.documents.aadhaarBack
    ) {
      return false;
    }

    return true;
  };

  // Enhanced missing fields check
  const checkMissingFields = (data) => {
    const missing = [];

    const basicInfo = data.basicInfo;
    if (!basicInfo.fullName) missing.push("Full Name");
    if (!basicInfo.email) missing.push("Email");
    if (!basicInfo.mobileNo) missing.push("Mobile Number");
    if (!basicInfo.panNumber) missing.push("PAN Number");
    if (!basicInfo.dateOfBirth) missing.push("Date of Birth"); // ADD: DOB check
    if (!basicInfo.line1) missing.push("Address Line 1");
    if (!basicInfo.city) missing.push("City");
    if (!basicInfo.state) missing.push("State");
    if (!basicInfo.pincode) missing.push("Pincode");
    if (!basicInfo.country) missing.push("Country");
    if (!basicInfo.nationality) missing.push("Nationality");

    // Enhanced demat checks
    if (!data.dematDetails.dpId) missing.push("DP ID");
    if (!data.dematDetails.clientId) missing.push("Client ID");
    if (!data.dematDetails.depository) missing.push("Depository"); // ADD: Depository check
    if (!data.dematDetails.dan) missing.push("DAN (Demat Account Number)"); // ADD: DAN check

    // Enhanced bank checks
    if (!data.bankDetails.bankName) missing.push("Bank Name");
    if (!data.bankDetails.accountNumber) missing.push("Account Number");
    if (!data.bankDetails.ifscCode) missing.push("IFSC Code");
    if (!data.bankDetails.accountName) missing.push("Account Holder Name"); // ADD: Account name check
    if (!data.bankDetails.accountType) missing.push("Account Type"); // ADD: Account type check

    if (!data.documents.panCard) missing.push("PAN Card Document");
    if (!data.documents.bankDocuments) missing.push("Bank Documents");
    if (!data.documents.dematDocuments) missing.push("Demat Documents");
    if (!data.documents.aadhaarFront) missing.push("Aadhaar Card Front");
    if (!data.documents.aadhaarBack) missing.push("Aadhaar Card Back");

    setMissingFields(missing);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const SectionTitle = ({ title }) => (
    <h3 className="text-lg sm:text-xl font-semibold text-blue-600 mb-4 border-b pb-2">
      {title}
    </h3>
  );

  const DataRow = ({
    label,
    value,
    documentType,
    canView,
    documentName,
    isRequired = true,
  }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 py-2 border-b">
      <span className="text-gray-600 font-medium min-w-[120px]">
        {label}
        {isRequired && <span className="text-red-500">*</span>}:
      </span>
      <span
        className={`flex-1 ${
          !value || value === "✗ Not uploaded"
            ? "text-red-500"
            : "text-gray-800"
        }`}
      >
        {value || "Not provided"}
      </span>
      {canView && value === "✓ Uploaded" && (
        <button
          onClick={() =>
            handleViewDocument(documentType, documentName || label)
          }
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-1.5 rounded text-sm"
          disabled={loadingStates[documentType]}
        >
          {loadingStates[documentType] ? "Loading..." : "View"}
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen px-4 sm:px-6 mt-15 lg:px-8">
      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 max-w-3xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold mb-2 text-center text-blue-500">
          Review Submission
        </h2>
        <p className="text-gray-500 text-center mb-6 text-sm sm:text-base">
          Please review your complete profile before final submission
        </p>

        {/* Missing Fields Warning Banner */}
        {missingFields.length > 0 && (
          <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 mb-6 rounded shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-orange-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">
                  You cannot submit your KYC until all required fields are
                  completed.
                </p>
                <div className="mt-2 text-sm">
                  <p className="font-semibold">Missing information:</p>
                  <ul className="list-disc list-inside mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                    {missingFields.map((field, index) => (
                      <li key={index} className="text-orange-800">
                        {field}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Basic Info - Enhanced */}
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <SectionTitle title="Basic Information" />
            <DataRow label="Full Name" value={formData.basicInfo.fullName} />
            <DataRow label="Email" value={formData.basicInfo.email} />
            <DataRow label="Mobile" value={formData.basicInfo.mobileNo} />
            <DataRow label="PAN Number" value={formData.basicInfo.panNumber} />
            {/* ADD: Date of Birth */}
            <DataRow
              label="Date of Birth"
              value={formatDate(formData.basicInfo.dateOfBirth)}
            />
            <DataRow label="Address Line 1" value={formData.basicInfo.line1} />
            <DataRow
              label="Address Line 2"
              value={formData.basicInfo.line2}
              isRequired={false}
            />
            <DataRow label="City" value={formData.basicInfo.city} />
            <DataRow label="State" value={formData.basicInfo.state} />
            <DataRow label="Pincode" value={formData.basicInfo.pincode} />
            <DataRow label="Country" value={formData.basicInfo.country} />
            <DataRow
              label="Nationality"
              value={formData.basicInfo.nationality}
            />
          </div>

          {/* Demat - Enhanced */}
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <SectionTitle title="Demat Account Details" />
            <DataRow label="DP ID" value={formData.dematDetails.dpId} />
            <DataRow label="Client ID" value={formData.dematDetails.clientId} />
            {/* ADD: Missing demat fields */}
            <DataRow
              label="Depository"
              value={formData.dematDetails.depository}
            />
            <DataRow
              label="DAN (Demat Account Number)"
              value={formData.dematDetails.dan}
              isRequired={false}
            />
          </div>

          {/* Bank - Enhanced */}
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <SectionTitle title="Bank Details" />
            <DataRow label="Bank Name" value={formData.bankDetails.bankName} />
            {/* ADD: Account Holder Name */}
            <DataRow
              label="Account Holder Name"
              value={formData.bankDetails.accountName}
            />
            <DataRow
              label="Account Number"
              value={formData.bankDetails.accountNumber}
            />
            <DataRow label="IFSC Code" value={formData.bankDetails.ifscCode} />
            {/* ADD: Account Type */}
            <DataRow
              label="Account Type"
              value={
                formData.bankDetails.accountType
                  ? formData.bankDetails.accountType.charAt(0).toUpperCase() +
                    formData.bankDetails.accountType.slice(1)
                  : ""
              }
            />
          </div>

          {/* Documents */}
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <SectionTitle title="Uploaded Documents" />
            <DataRow
              label="PAN Card"
              value={
                formData.documents.panCard ? "✓ Uploaded" : "✗ Not uploaded"
              }
              documentType="panCard"
              documentName="PAN Card"
              canView={formData.documents.panCard}
            />
            <DataRow
              label="Aadhaar Front"
              value={
                formData.documents.aadhaarFront ? "✓ Uploaded" : "✗ Not uploaded"
              }
              documentType="aadhaarFront"
              documentName="Aadhaar Front"
              canView={formData.documents.aadhaarFront}
            />
            <DataRow
              label="Aadhaar Back"
              value={
                formData.documents.aadhaarBack ? "✓ Uploaded" : "✗ Not uploaded"
              }
              documentType="aadhaarBack"
              documentName="Aadhaar Back"
              canView={formData.documents.aadhaarBack}
            />
            <DataRow
              label="Bank Documents"
              value={
                formData.documents.bankDocuments
                  ? "✓ Uploaded"
                  : "✗ Not uploaded"
              }
              documentType="bankDocuments"
              documentName="Bank Documents"
              canView={formData.documents.bankDocuments}
            />
            <DataRow
              label="Demat Documents"
              value={
                formData.documents.dematDocuments
                  ? "✓ Uploaded"
                  : "✗ Not uploaded"
              }
              documentType="dematDocuments"
              documentName="Demat Documents"
              canView={formData.documents.dematDocuments}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-100 border border-red-300 text-red-700 p-4 rounded">
              {error}
            </div>
          )}

          {/* Submit and Navigation Buttons */}
          {!["pending", "verified", "in-review"].includes(kycStatus) && (
            <>
              <div className="flex justify-center sm:justify-end mt-6">
                {allRequiredFieldsFilled() ? (
                  <button
                onClick={handleFinalSubmit}
                disabled={loading}
                className={`bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm sm:text-base font-medium transition
                  ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {loading ? "Submitting..." : "Submit KYC"}
              </button>
            ) : (
              <button
                onClick={() => {
                  toast.error(
                    "Please complete all required fields before submitting",
                    {
                      style: {
                        background: "#EF4444",
                        color: "#FFFFFF",
                      },
                    }
                  );
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="bg-gray-400 text-white px-6 py-2 rounded-lg text-sm sm:text-base font-medium"
              >
                Complete Profile to Submit
              </button>
            )}
          </div>

          {/* Navigation Button */}
          {!allRequiredFieldsFilled() && (
            <div className="flex justify-center mt-3">
              <button
                onClick={() => navigate("/profile/kyc/basic-info")}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
              >
                Go Back to Edit Profile
              </button>
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* Document Viewer Modal */}
      {viewerState.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Document Preview</h2>
              <button
                onClick={closeViewer}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <DocumentViewer
                kycId={viewerState.kycId}
                documentType={viewerState.documentType}
                documentName={viewerState.documentName}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewSubmission;
